import React, { useState, useEffect, useMemo, useContext } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import connect from '../../../utils/connect';
import sportsApi from '../../../utils/sportsApi';
import combineActions from '../../../utils/combineActions';
import dissociateSponsor from '../../../actions/settings/dissociateSponsor';

import Tooltip from '@app/ui/sports-components/Tooltip';
import '../../bulkSponsors.scss';
import { Context } from '../../contexts/OnboardingContext';
import { SponsorContext } from '../../contexts/SponsorContext';
import { getSponsorActive } from '../../../utils/getActiveOnboardingInstances';

import sendNotification from '../../../actions/notifications/send';

const Buttons = ({ client, sendNotification, dissociateSponsor }) => {
    const { t } = useTranslation('settings');
    const {
        allSponsors,
        rhId,
        isLoading,
        structureLocked,
        currentSelectedPlacement,
        setCurrentSponsorsList,
        sponsorsToAdd,
        placements,
        maxSponsors,
    } = useContext(Context);

    const {
        setSearchTerm,
        setUpdateFilters,
        sponsors,
        setSponsors,
        showSponsorSelector,
        setShowSponsorSelector,
        toAdd,
        setToAdd,
        toRemove,
        setToRemove,
        selected,
        setSelected,
        potentials,
        setPotentials,
        matchingSelected,
        toEdit,
        setToEdit,
        removeSponsor,
    } = useContext(SponsorContext);

    const [savingSponsor, setSavingSponsor] = useState(false);
    const hasMaxSponsors = !!maxSponsors;

    const resetPotentialsAndSelected = () => {
        const newPotentials = allSponsors
            .reduce((acc, sp) => {
                if (sponsors.findIndex(el => sp.id === el.id) > -1) return acc;
                acc.push(sp);
                return acc;
            }, [])
            .sort((a, b) => a.name.localeCompare(b.name));
        setPotentials(newPotentials);
        setSelected(sponsors.sort((a, b) => a.name.localeCompare(b.name)));
    };

    useEffect(() => {
        const newCurrentSponsorsList = matchingSelected.reduce((acc, sp) => {
            if (
                (currentSelectedPlacement &&
                    currentSelectedPlacement.sponsors.findIndex(
                        s => s.id === sp.id,
                    ) > -1) ||
                sponsorsToAdd.findIndex(s => s.id === sp.id) > -1
            )
                return acc;
            acc.push(sp);
            return acc;
        }, []);
        setCurrentSponsorsList(newCurrentSponsorsList);
    }, [currentSelectedPlacement, matchingSelected, sponsorsToAdd]);

    useEffect(() => {
        resetPotentialsAndSelected();
    }, [sponsors, allSponsors]);

    const showSelect = () => {
        setShowSponsorSelector(true);
        setSearchTerm('');
    };

    const cancelSponsor = () => {
        const confirmation = confirm(
            t('clientConfiguration.onboarding.cancelConfirmSponsor'),
        );
        if (confirmation) {
            setShowSponsorSelector(false);
            setToAdd([]);
            setToRemove([]);
            resetPotentialsAndSelected();
            setSearchTerm('');
        }
    };

    const addSponsor = entityIds => async () => {
        const payload = {
            association: 'add',
            entityIds,
        };

        await sportsApi
            .url(`/rightsholders/${rhId}/sponsors`)
            .json(payload)
            .post()
            .json();

        await sportsApi
            .url(`/clients/${client.id}/rightsholders/${rhId}/sponsors`)
            .json(payload)
            .post()
            .json();
    };

    const saveSponsor = async () => {
        const reset = () => {
            setUpdateFilters(true);
            setToAdd([]);
            setToRemove([]);
            setSavingSponsor(false);
            setShowSponsorSelector(false);
        };

        if (
            hasMaxSponsors &&
            sponsors.length + toAdd.length - toRemove.length > maxSponsors
        ) {
            sendNotification(
                t('clientConfiguration.onboarding.notifications.sponsorCapacityReached'),
                {
                    context: 'danger',
                    description:
                        t('clientConfiguration.onboarding.deactivateErrorContact'),
                },
            );
            return;
        }

        try {
            setSavingSponsor(true);
            setSearchTerm('');

            if (toRemove.length > 0) {
                const confirmation = confirm(
                    t('clientConfiguration.onboarding.removeSponsorConfirm'),
                );

                if (!confirmation) {
                    reset();
                    return;
                }

                const dissociatePromises = toRemove.map(el =>
                    dissociateAll(el.id)(),
                );

                const hasDissociatedArray =
                    await Promise.all(dissociatePromises);
                const hasDissociated = hasDissociatedArray.reduce((acc, el) => {
                    return acc && el;
                }, true);

                if (!hasDissociated) return;

                await Promise.all(toRemove.map(sp => removeSponsor(sp)()));
                setUpdateFilters(true);

                setSelected([...selected, ...toRemove]);
                const newPotentials = potentials.reduce((acc, s) => {
                    if (toRemove.findIndex(el => s.id === el.id) > -1)
                        return acc;
                    acc.push(s);
                    return acc;
                }, []);
                setPotentials(newPotentials);
            }

            if (toAdd.length > 0) {
                const entityIds = toAdd.reduce((acc, el) => {
                    acc.push(el.id);
                    return acc;
                }, []);
                await addSponsor(entityIds)();
            }

            let newSponsors = sponsors.reduce((acc, sp) => {
                if (toRemove.findIndex(el => sp.id === el.id) > -1) return acc;
                acc.push(sp);
                return acc;
            }, []);

            newSponsors = [...newSponsors, ...toAdd];

            setSponsors(newSponsors);

            reset();
        } catch (err) {
            // eslint-disable-next-line no-console
            console.log(err);
        }
    };

    const dissociateAll = sponsorId => async () => {
        try {
            const entityIds = placements.reduce((acc, p) => {
                if (p.sponsors.findIndex(s => s.id === sponsorId) > -1)
                    acc.push(p.id);
                return acc;
            }, []);

            if (entityIds.length === 0) return true;

            const payload = {
                association: 'remove',
                entityIds,
            };

            await sportsApi
                .url(
                    `/clients/${client.id}/rightsholders/${rhId}/sponsors/${sponsorId}/placements`,
                )
                .json(payload)
                .post()
                .json();

            await dissociateSponsor({ rhId, sponsorId });
            return true;
        } catch (e) {
            // eslint-disable-next-line no-console
            console.log(e);
        }
    };

    const activeSponsorsCount = useMemo(
        () => sponsors.filter(sp => getSponsorActive(sp)).length,
        [sponsors],
    );
    const hasReachedMaxSponsors =
        hasMaxSponsors && activeSponsorsCount >= maxSponsors;

    return (
        <div className="gds-flex__item gds-flex__item--grow-0">
            {!toEdit && !showSponsorSelector && (
                <Tooltip
                    text={
                        hasReachedMaxSponsors
                            ? t('clientConfiguration.onboarding.capacityReachedSponsors')
                            : ''
                    }
                    position="top-left"
                >
                    <button
                        type="button"
                        className="gds-button gds-button--sm gds-button--secondary"
                        onClick={showSelect}
                        disabled={
                            structureLocked || isLoading || hasReachedMaxSponsors
                        }
                    >
                        {t('clientConfiguration.onboarding.addSponsor')}
                    </button>
                </Tooltip>
            )}
            {!toEdit && showSponsorSelector && (
                <div className="gds-flex__item gds-flex__item--grow-0">
                    <button
                        type="button"
                        className="gds-button gds-button--sm gds-button--default -m-r-1"
                        onClick={cancelSponsor}
                        disabled={structureLocked || savingSponsor || isLoading}
                    >
                        {t('clientConfiguration.onboarding.cancel')}
                    </button>

                    <button
                        type="button"
                        className="gds-button gds-button--sm gds-button--secondary"
                        onClick={saveSponsor}
                        disabled={structureLocked || savingSponsor || isLoading}
                    >
                        {t('clientConfiguration.onboarding.saveChanges')}
                    </button>
                </div>
            )}
            {toEdit && (
                <button
                    type="button"
                    className="gds-button gds-button--sm gds-button--secondary"
                    onClick={() => setToEdit(null)}
                >
                    {t('clientConfiguration.onboarding.save')}
                </button>
            )}
        </div>
    );
};

Buttons.propTypes = {
    client: PropTypes.object,
    sendNotification: PropTypes.func,
    dissociateSponsor: PropTypes.func,
};

export default connect(
    ['client'],
    combineActions({
        dissociateSponsor,
        sendNotification,
    }),
)(Buttons);
